import {
	createActionsHook,
	createSelector,
	createHook,
	createStateHook,
	createStore,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type HookStateFunction,
	type Store,
	type StoreActionApi,
} from 'react-sweet-state';

import { fg } from '@atlaskit/platform-feature-flags';
import type {
	AgentKnowledgeConfiguration,
	AgentKnowledgeConfigurationResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import {
	useConnectorNudgeActionsFromRovoConfig,
	useConnectorsDataReadyFromRovoConfig,
	useConnectorsFromRovoConfig,
	useConnectorsIsFetchingFromRovoConfig,
	useEnabledKnowledgeSourcesFromRovoConfig,
	useKnowledgeSourcesActionsFromRovoConfig,
	useKnowledgeSourcesStoreFromRovoConfig,
	useNewlyEnabledConnectorsFromRovoConfig,
} from './rovo-connectors-config';

/**
 * Per-connector state that collocates all properties for a single connector.
 * Extends the knowledge configuration from the assistance service with
 * additional metadata sourced from the 3P config API and local tracking.
 */
export type ConnectorState = AgentKnowledgeConfiguration & {
	/** Epoch ms when the user last interacted with this app (selected in filter or mentioned in chat). */
	lastUsedAt?: number;
};

export type ConnectorsStoreState = {
	/** Map of connector source to its full state. */
	connectors: Record<string, ConnectorState>;
	isFetching: boolean;
	error: Error | null;
	lastFetchedAt: number | null;
	/** True after the first fetch attempt (success or failure). Prevents automatic retries on unrecoverable errors. */
	hasFetchAttempted: boolean;
};

const LS_KEY = 'rovo-connectors-metadata';

type PersistedMetadata = {
	lastUsedAt: Record<string, number>;
};

const getPersistedMetadata = (): PersistedMetadata => {
	try {
		const raw = localStorage.getItem(LS_KEY);
		if (raw) {
			const parsed = JSON.parse(raw);
			if (parsed && typeof parsed === 'object') {
				return {
					lastUsedAt: parsed.lastUsedAt ?? {},
				};
			}
		}
	} catch {
		// localStorage may be unavailable or corrupted
	}
	return { lastUsedAt: {} };
};

const persistMetadata = (connectors: Record<string, ConnectorState>): void => {
	try {
		const metadata: PersistedMetadata = {
			lastUsedAt: {},
		};
		for (const [source, connector] of Object.entries(connectors)) {
			if (connector.lastUsedAt) {
				metadata.lastUsedAt[source] = connector.lastUsedAt;
			}
		}
		localStorage.setItem(LS_KEY, JSON.stringify(metadata));
	} catch {
		// localStorage may be unavailable
	}
};

const initialState: ConnectorsStoreState = {
	connectors: {},
	isFetching: false,
	error: null,
	lastFetchedAt: null,
	hasFetchAttempted: false,
};

type ConnectorsStoreActions = {
	setKnowledgeSources: (
		response: AgentKnowledgeConfigurationResponse,
	) => ({ getState, setState }: StoreActionApi<ConnectorsStoreState>) => void;
	setError: (error: Error) => ({ setState }: StoreActionApi<ConnectorsStoreState>) => void;
	markUserConnected: (
		source: string,
	) => ({ getState, setState }: StoreActionApi<ConnectorsStoreState>) => void;
	markLastUsed: (
		source: string,
	) => ({ getState, setState }: StoreActionApi<ConnectorsStoreState>) => void;
	fetchIfEmpty: (
		fetcher: () => Promise<AgentKnowledgeConfigurationResponse>,
		siteId: string,
	) => ({ getState, setState, dispatch }: StoreActionApi<ConnectorsStoreState>) => Promise<void>;
	prefetchIfEmpty: (
		fetcher: () => Promise<AgentKnowledgeConfigurationResponse>,
		siteId: string,
		callbacks?: {
			onStart?: () => void;
			onSuccess?: () => void;
			onError?: (error: Error) => void;
		},
	) => ({ getState, setState, dispatch }: StoreActionApi<ConnectorsStoreState>) => Promise<void>;
};

const store: Store<ConnectorsStoreState, ConnectorsStoreActions> = createStore({
	name: 'connectorsStore',
	initialState,
	actions: {
		setKnowledgeSources:
			(
				response: AgentKnowledgeConfigurationResponse,
			): (({ getState, setState }: StoreActionApi<ConnectorsStoreState>) => void) =>
			({ getState, setState }) => {
				const existing = getState().connectors;
				const persisted = getPersistedMetadata();
				const now = Date.now();
				const connectors: Record<string, ConnectorState> = {};

				response.knowledgeSources.forEach((source) => {
					const prev = existing[source.source];
					connectors[source.source] = {
						...source,
						lastUsedAt: prev?.lastUsedAt ?? persisted.lastUsedAt[source.source],
					};
				});

				const nextState: ConnectorsStoreState = {
					connectors,
					isFetching: false,
					error: null,
					lastFetchedAt: now,
					hasFetchAttempted: true,
				};
				setState(nextState);
			},
		setError:
			(error: Error): (({ setState }: StoreActionApi<ConnectorsStoreState>) => void) =>
			({ setState }) => {
				setState({
					error,
					isFetching: false,
				});
			},
		markUserConnected:
			(source: string): (({ getState, setState }: StoreActionApi<ConnectorsStoreState>) => void) =>
			({ getState, setState }) => {
				const existing = getState().connectors[source];
				if (!existing) {
					return;
				}
				const nextState = {
					...getState(),
					connectors: {
						...getState().connectors,
						[source]: { ...existing, isUserOAuthed: true },
					},
				};
				setState(nextState);
				persistMetadata(nextState.connectors);
			},
		markLastUsed:
			(source: string): (({ getState, setState }: StoreActionApi<ConnectorsStoreState>) => void) =>
			({ getState, setState }) => {
				const existing = getState().connectors[source];
				if (!existing) {
					return;
				}
				const nextState = {
					...getState(),
					connectors: {
						...getState().connectors,
						[source]: { ...existing, lastUsedAt: Date.now() },
					},
				};
				setState(nextState);
				persistMetadata(nextState.connectors);
			},
		fetchIfEmpty:
			(
				fetcher: () => Promise<AgentKnowledgeConfigurationResponse>,
				// `siteId` is threaded through by callers so the rovo-config FG path
				// (see ./rovo-connectors-config.ts) can scope the fetch to a tenant.
				// The legacy knowledge-store path here does not need it: the tenant is
				// already baked into the `fetcher` closure's request headers.
				_siteId: string,
			): (({
				getState,
				setState,
				dispatch,
			}: StoreActionApi<ConnectorsStoreState>) => Promise<void>) =>
			async ({ getState, setState, dispatch }) => {
				const { isFetching, lastFetchedAt, hasFetchAttempted } = getState();
				if (isFetching || lastFetchedAt !== null || hasFetchAttempted) {
					return;
				}
				setState({ isFetching: true, hasFetchAttempted: true });
				try {
					const response = await fetcher();
					dispatch(store.actions.setKnowledgeSources(response));
				} catch (error) {
					dispatch(
						store.actions.setError(error instanceof Error ? error : new Error(String(error))),
					);
				}
			},
		prefetchIfEmpty:
			(
				fetcher: () => Promise<AgentKnowledgeConfigurationResponse>,
				// See `fetchIfEmpty` above — `siteId` is consumed by the rovo-config FG
				// path, not by this legacy knowledge-store implementation.
				_siteId: string,
				callbacks?: {
					onStart?: () => void;
					onSuccess?: () => void;
					onError?: (error: Error) => void;
				},
			): (({
				getState,
				setState,
				dispatch,
			}: StoreActionApi<ConnectorsStoreState>) => Promise<void>) =>
			async ({ getState, setState, dispatch }) => {
				const { isFetching, lastFetchedAt, hasFetchAttempted } = getState();
				if (isFetching || lastFetchedAt !== null || hasFetchAttempted) {
					return;
				}

				callbacks?.onStart?.();
				setState({ isFetching: true });

				try {
					const response = await fetcher();
					dispatch(store.actions.setKnowledgeSources(response));
					callbacks?.onSuccess?.();
				} catch (error) {
					const normalizedError = error instanceof Error ? error : new Error(String(error));
					setState({
						error: normalizedError,
						isFetching: false,
						hasFetchAttempted: false,
					});
					callbacks?.onError?.(normalizedError);
				}
			},
	},
});

export type BoundConnectorsStoreActions = BoundActions<
	ConnectorsStoreState,
	ConnectorsStoreActions
>;

// ---------------------------------------------------------------------------
// RAOC-23 migration shim
// ---------------------------------------------------------------------------
// Every selector / action hook exported from this module is split into a
// `*FromKnowledge` variant (the legacy react-sweet-state path, defined
// in this file) and a `*FromRovoConfig` variant (sourced from
// `useRovoConfigStore` in `@atlassian/rovo-connectors-config`, defined in
// the co-located `./rovo-connectors-config.ts` module). The public name
// (e.g. `useEnabledKnowledgeSources`) is a thin wrapper that branches on
// the `rovo_cas_use_rovo_config` feature gate at render time.
//
// TODO RAOC-23: once `rovo_cas_use_rovo_config` is fully rolled out, inline
//               the `*FromRovoConfig` body back into each public hook,
//               delete the `*FromKnowledge` variants and the
//               `react-sweet-state` store above, and delete the
//               `./rovo-connectors-config.ts` module.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// useKnowledgeSourcesStore
// ---------------------------------------------------------------------------

/**
 * @internal Consumers must use `useKnowledgeSourcesStore` directly.
 */
export const useKnowledgeSourcesStoreFromKnowledge: HookFunction<
	ConnectorsStoreState,
	BoundConnectorsStoreActions,
	void
> = createHook(store);

export const useKnowledgeSourcesStore: HookFunction<
	ConnectorsStoreState,
	BoundConnectorsStoreActions,
	void
> = ((...args: Parameters<typeof useKnowledgeSourcesStoreFromKnowledge>) =>
	fg('rovo_cas_use_rovo_config')
		? useKnowledgeSourcesStoreFromRovoConfig() // eslint-disable-line react-hooks/rules-of-hooks -- FG-gated branch; fg() is stable per session
		: useKnowledgeSourcesStoreFromKnowledge(...args)) as HookFunction<
	// eslint-disable-line react-hooks/rules-of-hooks -- FG-gated branch; fg() is stable per session
	ConnectorsStoreState,
	BoundConnectorsStoreActions,
	void
>;

// ---------------------------------------------------------------------------
// useKnowledgeSourcesActions
// ---------------------------------------------------------------------------

/**
 * @internal Consumers must use `useKnowledgeSourcesActions` directly.
 */
export const useKnowledgeSourcesActionsFromKnowledge: HookActionsFunction<BoundConnectorsStoreActions> =
	createActionsHook(store);

export const useKnowledgeSourcesActions: HookActionsFunction<BoundConnectorsStoreActions> = () =>
	fg('rovo_cas_use_rovo_config')
		? useKnowledgeSourcesActionsFromRovoConfig() // eslint-disable-line react-hooks/rules-of-hooks -- FG-gated branch; fg() is stable per session
		: useKnowledgeSourcesActionsFromKnowledge(); // eslint-disable-line react-hooks/rules-of-hooks -- FG-gated branch; fg() is stable per session

// ---------------------------------------------------------------------------
// useConnectors
// ---------------------------------------------------------------------------

/**
 * @internal Consumers must use `useConnectors` directly.
 */
export const useConnectorsFromKnowledge: HookFunction<
	Record<string, ConnectorState>,
	BoundConnectorsStoreActions,
	void
> = createHook(store, {
	selector: (state) => state.connectors,
});

export const useConnectors: HookFunction<
	Record<string, ConnectorState>,
	BoundConnectorsStoreActions,
	void
> = (...args: Parameters<typeof useConnectorsFromKnowledge>) =>
	fg('rovo_cas_use_rovo_config')
		? useConnectorsFromRovoConfig() // eslint-disable-line react-hooks/rules-of-hooks -- FG-gated branch; fg() is stable per session
		: useConnectorsFromKnowledge(...args); // eslint-disable-line react-hooks/rules-of-hooks -- FG-gated branch; fg() is stable per session

// ---------------------------------------------------------------------------
// useEnabledKnowledgeSources
// ---------------------------------------------------------------------------

/**
 * @internal Consumers must use `useEnabledKnowledgeSources` directly.
 */
export const useEnabledKnowledgeSourcesFromKnowledge: HookFunction<
	ConnectorState[],
	BoundConnectorsStoreActions,
	void
> = createHook(store, {
	selector: (state) => Object.values(state.connectors),
});

export const useEnabledKnowledgeSources: HookFunction<
	ConnectorState[],
	BoundConnectorsStoreActions,
	void
> = (...args: Parameters<typeof useEnabledKnowledgeSourcesFromKnowledge>) =>
	fg('rovo_cas_use_rovo_config')
		? useEnabledKnowledgeSourcesFromRovoConfig() // eslint-disable-line react-hooks/rules-of-hooks -- FG-gated branch; fg() is stable per session
		: useEnabledKnowledgeSourcesFromKnowledge(...args); // eslint-disable-line react-hooks/rules-of-hooks -- FG-gated branch; fg() is stable per session

// ---------------------------------------------------------------------------
// useConnectorsIsFetching
// ---------------------------------------------------------------------------

/**
 * @internal Consumers must use `useConnectorsIsFetching` directly.
 */
export const useConnectorsIsFetchingFromKnowledge: HookFunction<
	boolean,
	BoundConnectorsStoreActions,
	void
> = createHook(store, {
	selector: (state) => state.isFetching,
});

export const useConnectorsIsFetching: HookFunction<boolean, BoundConnectorsStoreActions, void> = (
	...args: Parameters<typeof useConnectorsIsFetchingFromKnowledge>
) =>
	fg('rovo_cas_use_rovo_config')
		? useConnectorsIsFetchingFromRovoConfig() // eslint-disable-line react-hooks/rules-of-hooks -- FG-gated branch; fg() is stable per session
		: useConnectorsIsFetchingFromKnowledge(...args); // eslint-disable-line react-hooks/rules-of-hooks -- FG-gated branch; fg() is stable per session

// ---------------------------------------------------------------------------
// useConnectorsDataReady
// ---------------------------------------------------------------------------

/**
 * @internal Consumers must use `useConnectorsDataReady` directly.
 */
export const useConnectorsDataReadyFromKnowledge: HookFunction<
	boolean,
	BoundConnectorsStoreActions,
	void
> = createHook(store, {
	selector: (state) => state.hasFetchAttempted && !state.isFetching,
});

/**
 * Returns true once the connectors store has completed its first fetch attempt
 * and is not currently re-fetching. Use this to guard UI that should not render
 * until connector data is available — avoids flashing an empty state while the
 * store is in its initial loading window.
 */
export const useConnectorsDataReady: HookFunction<boolean, BoundConnectorsStoreActions, void> = (
	...args: Parameters<typeof useConnectorsDataReadyFromKnowledge>
) =>
	fg('rovo_cas_use_rovo_config')
		? useConnectorsDataReadyFromRovoConfig() // eslint-disable-line react-hooks/rules-of-hooks -- FG-gated branch; fg() is stable per session
		: useConnectorsDataReadyFromKnowledge(...args); // eslint-disable-line react-hooks/rules-of-hooks -- FG-gated branch; fg() is stable per session
// ---------------------------------------------------------------------------
// isNewlyEnabledApp + useNewlyEnabledConnectors
// ---------------------------------------------------------------------------

const NEW_APP_WINDOW_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export const isNewlyEnabledApp = (
	connector: Pick<ConnectorState, 'isUserOAuthed' | 'connectorCreatedAt'>,
): boolean => {
	if (connector.isUserOAuthed) {
		return false;
	}
	if (!connector.connectorCreatedAt) {
		return false;
	}
	const enabledAt = new Date(connector.connectorCreatedAt).getTime();
	if (isNaN(enabledAt)) {
		return false;
	}
	return Date.now() - enabledAt <= NEW_APP_WINDOW_MS;
};

const newlyEnabledConnectorsSelector = createSelector(
	(state: ConnectorsStoreState) => state.connectors,
	(connectors) => Object.values(connectors).filter(isNewlyEnabledApp),
);

/**
 * @internal Consumers must use `useNewlyEnabledConnectors` directly.
 */
export const useNewlyEnabledConnectorsFromKnowledge: HookStateFunction<ConnectorState[], void> =
	createStateHook(store, {
		selector: newlyEnabledConnectorsSelector,
	});

export const useNewlyEnabledConnectors: HookStateFunction<ConnectorState[], void> = (
	...args: Parameters<typeof useNewlyEnabledConnectorsFromKnowledge>
) =>
	fg('rovo_cas_use_rovo_config')
		? useNewlyEnabledConnectorsFromRovoConfig() // eslint-disable-line react-hooks/rules-of-hooks -- FG-gated branch; fg() is stable per session
		: useNewlyEnabledConnectorsFromKnowledge(...args); // eslint-disable-line react-hooks/rules-of-hooks -- FG-gated branch; fg() is stable per session

// ---------------------------------------------------------------------------
// useConnectorNudgeActions
// ---------------------------------------------------------------------------

/**
 * @internal Consumers must use `useConnectorNudgeActions` directly.
 */
export const useConnectorNudgeActionsFromKnowledge: HookActionsFunction<
	Pick<BoundConnectorsStoreActions, 'markUserConnected'>
> = createActionsHook(store);

export const useConnectorNudgeActions: HookActionsFunction<
	Pick<BoundConnectorsStoreActions, 'markUserConnected'>
> = () =>
	fg('rovo_cas_use_rovo_config')
		? useConnectorNudgeActionsFromRovoConfig() // eslint-disable-line react-hooks/rules-of-hooks -- FG-gated branch; fg() is stable per session
		: useConnectorNudgeActionsFromKnowledge(); // eslint-disable-line react-hooks/rules-of-hooks -- FG-gated branch; fg() is stable per session

export { store as connectorsStore };
